import styled from 'styled-components';

import { COLORS, ACQ, CAMPAIGN_DETAIL, MOBILE } from '../../theme/tokens';
import { coverImage, hoverWash } from '../../theme/mixins';
import { Text } from '../../components';

// --- Intro (centered) --------------------------------------------------------
export const Intro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

export const Period = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.1;
  text-transform: uppercase;
  text-align: center;
  color: ${ACQ.white};
`;

export const IntroText = styled(Text)`
  font-size: 14px;
  font-weight: 300;
  line-height: 1.4;
  text-align: center;
  color: ${ACQ.t200};
`;

// --- Offering section (dark panel + channels below it) -----------------------
export const OfferingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const OfferingPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 16px;
  background: ${ACQ.campCard};
`;

export const OfferingEyebrow = styled(Text)`
  font-size: 14px;
  font-weight: 300;
  line-height: 1;
  color: ${ACQ.chdPeriod};
`;

export const OfferingHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

export const OfferingTitle = styled(Text)`
  font-size: 22px;
  font-weight: 500;
  line-height: 1.2;
  color: ${ACQ.t200};
`;

export const BadgeFree = styled(Text)`
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.33;
  white-space: nowrap;
  color: ${ACQ.success};
`;

export const Description = styled(Text)`
  font-size: 12px;
  font-weight: 300;
  line-height: 1.34;
  letter-spacing: -0.24px;
  color: ${ACQ.t300};
`;

// --- Channels (label + avatar pills + "+N more" cluster) ---------------------
export const Channels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ChannelsLabel = styled(Text)`
  font-size: 14px;
  font-weight: 300;
  line-height: 1.2;
  color: ${ACQ.t200};
`;

export const ChannelsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const ChannelPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 3px 16px 3px 3px;
  border-radius: 99px;
  background: ${COLORS.planCardBg};
`;

export const ChannelAvatar = styled.span`
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: ${ACQ.white};
`;

export const ChannelName = styled(Text)`
  font-size: 14px;
  font-weight: 300;
  line-height: 1;
  white-space: nowrap;
  color: ${ACQ.white};
`;

export const MoreCluster = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const MoreAvatars = styled.div`
  display: flex;
  align-items: center;
`;

export const MoreAvatar = styled.span<{ $bg: string; $i: number }>`
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1px solid ${COLORS.hairline};
  background: ${(p) => p.$bg};
  margin-left: ${(p) => (p.$i === 0 ? 0 : -14)}px;
`;

export const MoreText = styled(Text)`
  font-size: 11px;
  font-weight: 400;
  line-height: 1.1;
  white-space: nowrap;
  background: ${CAMPAIGN_DETAIL.moreText};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

// --- Step sections (STEP 1 cards, STEP 2 activate) ---------------------------
export const Step = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const StepTitle = styled(Text)`
  font-size: 14px;
  font-weight: 300;
  line-height: 1;
  text-align: center;
  color: ${ACQ.chdPeriod};
`;

export const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  ${MOBILE} {
    gap: 16px;
  }
`;

// --- Style-option card -------------------------------------------------------
export const Card = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border-radius: 22px;
  cursor: pointer;
  text-align: left;
  background: ${(p) => (p.$selected ? `${CAMPAIGN_DETAIL.cardSelectedGlow}, ${CAMPAIGN_DETAIL.cardBg}` : CAMPAIGN_DETAIL.cardBg)};
  border: ${(p) => (p.$selected ? `2px solid ${ACQ.orange}` : `1px solid ${ACQ.hairline}`)};
  transition: border-color 150ms ease;
  ${hoverWash}
`;

export const CardHeader = styled(Text)`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.67;
  color: ${ACQ.chdPeriod};
`;

export const CardImage = styled.div<{ $src: string }>`
  width: 100%;
  aspect-ratio: 1024 / 683;
  border-radius: 16px;
  ${(p) => coverImage(p.$src)}
`;

export const CardFooter = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  font-size: 14px;
  color: ${(p) => (p.$selected ? ACQ.success : ACQ.orange)};
`;

export const CardFooterLabel = styled(Text)`
  line-height: 1.4;
`;

// --- STEP 2 — activate -------------------------------------------------------
export const ActivateInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

export const ActivateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 48px;
  padding: 0 24px;
  border: none;
  border-radius: 14px;
  background: ${ACQ.orange};
  color: ${COLORS.ctaSolidText};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}
`;

export const ActivateLabel = styled(Text)`
  line-height: 1.5;
`;

export const Warning = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const WarningText = styled(Text)`
  font-size: 12px;
  line-height: 1.34;
  color: ${ACQ.t400};
`;
